import React, { useEffect, useState, useMemo } from 'react'
import {
  collection, doc, updateDoc, deleteDoc, onSnapshot, setDoc, serverTimestamp
} from 'firebase/firestore'
import { initializeApp, getApps } from 'firebase/app'
import { getAuth, createUserWithEmailAndPassword } from 'firebase/auth'
import { auth, db } from '../../firebase/firebase'
import { useAuth } from '../../contexts/AuthContext'
import AdminLayout from './AdminLayout'

// Secondary Firebase app — used to create users without signing out the current admin
const secondaryApp = getApps().find(a => a.name === 'secondary')
  || initializeApp({
      apiKey:            process.env.REACT_APP_FIREBASE_API_KEY,
      authDomain:        process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
      projectId:         process.env.REACT_APP_FIREBASE_PROJECT_ID,
      storageBucket:     process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
      messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
      appId:             process.env.REACT_APP_FIREBASE_APP_ID,
    }, 'secondary')
const secondaryAuth = getAuth(secondaryApp)

const PAGE_SIZE = 10
const emptyForm = { email: '', password: '', displayName: '', roleId: '', permissions: [] }

const AdminUsers = () => {
  const { currentUser } = useAuth()
  const [users,    setUsers]    = useState([])
  const [roles,    setRoles]    = useState([])
  const [form,     setForm]     = useState(emptyForm)
  const [editingId, setEditingId] = useState(null)
  const [saving,   setSaving]   = useState(false)
  const [deleting, setDeleting] = useState(null)
  const [toggling, setToggling] = useState(null)
  const [showForm, setShowForm] = useState(false)

  // Filter + pagination state
  const [search,     setSearch]     = useState('')
  const [roleFilter, setRoleFilter] = useState('')
  const [page,       setPage]       = useState(1)

  useEffect(() => {
    const u1 = onSnapshot(collection(db, 'users'), snap =>
      setUsers(snap.docs.map(d => ({ id: d.id, ...d.data() }))))
    const u2 = onSnapshot(collection(db, 'roles'), snap =>
      setRoles(snap.docs.map(d => ({ id: d.id, ...d.data() }))))
    return () => { u1(); u2() }
  }, [])

  // ── Filtered + paginated list ────────────────────────────────────────────────
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase()
    return users.filter(u => {
      const matchName = !q ||
        (u.displayName || '').toLowerCase().includes(q) ||
        (u.email || '').toLowerCase().includes(q)
      const matchRole = !roleFilter || u.roleId === roleFilter
      return matchName && matchRole
    })
  }, [users, search, roleFilter])

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE))
  const paginated  = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE)

  const handleFilterChange = (setter) => (e) => { setter(e.target.value); setPage(1) }

  // ── Form helpers ─────────────────────────────────────────────────────────────
  const isSelfEditing = editingId && users.find(u => u.id === editingId)?.uid === currentUser?.uid

  const handleRoleChange = (roleId) => {
    if (isSelfEditing) return
    const role = roles.find(r => r.id === roleId)
    setForm(f => ({ ...f, roleId, permissions: role?.permissions || [] }))
  }

  const handleSave = async (e) => {
    e.preventDefault()
    setSaving(true)
    try {
      if (editingId) {
        const patch = { displayName: form.displayName.trim() }
        if (!isSelfEditing) { patch.roleId = form.roleId; patch.permissions = form.permissions }
        await updateDoc(doc(db, 'users', editingId), patch)
      } else {
        const cred = await createUserWithEmailAndPassword(secondaryAuth, form.email, form.password)
        await setDoc(doc(db, 'users', cred.user.uid), {
          uid: cred.user.uid,
          email: form.email,
          displayName: form.displayName.trim(),
          roleId: form.roleId,
          permissions: form.permissions,
          active: true,
          createdAt: serverTimestamp(),
        })
        await secondaryAuth.signOut()
      }
      setForm(emptyForm); setEditingId(null); setShowForm(false)
    } catch (err) {
      alert('Помилка: ' + err.message)
    } finally {
      setSaving(false)
    }
  }

  const handleEdit = (user) => {
    setEditingId(user.id)
    setForm({ email: user.email, password: '', displayName: user.displayName || '', roleId: user.roleId || '', permissions: user.permissions || [] })
    setShowForm(true)
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  const handleDelete = async (user) => {
    if (user.uid === currentUser?.uid) return
    if (!window.confirm(
      `Видалити профіль ${user.email} з бази?\n\n` +
      `Увага: акаунт залишиться в Firebase Authentication. ` +
      `Щоб потім додати цей email знову — спочатку видаліть його вручну: ` +
      `Firebase Console → Authentication → знайти email → видалити.`
    )) return
    setDeleting(user.id)
    try {
      await deleteDoc(doc(db, 'users', user.id))
    } catch (err) {
      alert('Помилка видалення: ' + err.message)
    } finally {
      setDeleting(null)
    }
  }

  const handleToggleActive = async (user) => {
    if (user.uid === currentUser?.uid) return
    const next = user.active === false
    setToggling(user.id)
    try {
      await updateDoc(doc(db, 'users', user.id), { active: next })
    } catch (err) {
      alert('Помилка: ' + err.message)
    } finally {
      setToggling(null)
    }
  }

  const initials = (name) => (name || '?').split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2)

  // ── Render ───────────────────────────────────────────────────────────────────
  return (
    <AdminLayout>
      <div style={s.page}>
        <div style={s.pageHeader}>
          <div>
            <h1 style={s.pageTitle}>Користувачі</h1>
            <p style={s.pageDesc}>Керуйте адміністраторами та їхніми правами доступу</p>
          </div>
          {!showForm && (
            <button onClick={() => { setForm(emptyForm); setEditingId(null); setShowForm(true) }} style={s.btnPrimary}>
              + Новий користувач
            </button>
          )}
        </div>

        {/* ── Form ── */}
        {showForm && (
          <div style={{ ...s.card, marginBottom: '24px' }}>
            <div style={s.cardHeader}>
              <span style={s.cardIcon}>👤</span>
              <span style={s.cardTitle}>{editingId ? 'Редагувати користувача' : 'Новий користувач'}</span>
            </div>
            <form onSubmit={handleSave} style={s.formReset}>
              <div style={s.formGrid}>
                <div style={s.field}>
                  <label style={s.label}>Ім'я</label>
                  <input
                    value={form.displayName}
                    onChange={e => setForm(f => ({ ...f, displayName: e.target.value }))}
                    style={s.input}
                    placeholder="Ім'я та прізвище"
                  />
                </div>
                {!editingId && (
                  <>
                    <div style={s.field}>
                      <label style={s.label}>Email</label>
                      <input type='email' value={form.email}
                        onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
                        style={s.input} required placeholder='user@example.com' />
                    </div>
                    <div style={s.field}>
                      <label style={s.label}>Пароль</label>
                      <input type='password' value={form.password}
                        onChange={e => setForm(f => ({ ...f, password: e.target.value }))}
                        style={s.input} required placeholder='Мінімум 6 символів' minLength={6} />
                    </div>
                  </>
                )}
                <div style={s.field}>
                  <label style={s.label}>
                    Роль
                    {isSelfEditing && <span style={s.selfNote}> — не можна змінити власну роль</span>}
                  </label>
                  <select
                    value={form.roleId}
                    onChange={e => handleRoleChange(e.target.value)}
                    style={{ ...s.input, ...(isSelfEditing ? s.inputDisabled : {}) }}
                    disabled={!!isSelfEditing}
                  >
                    <option value=''>— без ролі —</option>
                    {roles.map(r => <option key={r.id} value={r.id}>{r.name}</option>)}
                  </select>
                </div>
              </div>
              <div style={s.btnRow}>
                <button type='submit' disabled={saving} style={s.btnPrimary}>
                  {saving ? 'Збереження…' : editingId ? 'Зберегти зміни' : 'Створити'}
                </button>
                <button type='button'
                  onClick={() => { setForm(emptyForm); setEditingId(null); setShowForm(false) }}
                  style={s.btnGhost}>
                  Скасувати
                </button>
              </div>
            </form>
          </div>
        )}

        {/* ── Users list ── */}
        <div style={s.card}>
          <div style={s.cardHeader}>
            <span style={s.cardIcon}>👥</span>
            <span style={s.cardTitle}>Всі користувачі</span>
            <span style={s.badge}>{filtered.length}</span>
          </div>

          {/* Filters */}
          <div style={s.filters}>
            <input
              style={{ ...s.input, flex: 1 }}
              placeholder="Пошук за ім'ям або email…"
              value={search}
              onChange={handleFilterChange(setSearch)}
            />
            <select
              style={{ ...s.input, minWidth: '160px' }}
              value={roleFilter}
              onChange={handleFilterChange(setRoleFilter)}
            >
              <option value=''>Всі ролі</option>
              {roles.map(r => <option key={r.id} value={r.id}>{r.name}</option>)}
            </select>
          </div>

          {paginated.length === 0 ? (
            <div style={s.empty}>{search || roleFilter ? 'Нічого не знайдено' : 'Користувачів ще немає'}</div>
          ) : (
            <div style={s.userList}>
              {paginated.map(user => {
                const role        = roles.find(r => r.id === user.roleId)
                const isSelf      = user.uid === currentUser?.uid
                const isInactive  = user.active === false

                return (
                  <div key={user.id} style={{ ...s.userRow, ...(isInactive ? s.userRowInactive : {}) }}>
                    <div style={{ ...s.userAvatar, ...(isInactive ? s.userAvatarInactive : {}) }}>
                      {initials(user.displayName || user.email)}
                    </div>
                    <div style={s.userInfo}>
                      <div style={s.userName}>
                        {user.displayName || <em style={{ color: '#b8a090' }}>Без імені</em>}
                        {isSelf && <span style={s.youTag}>ви</span>}
                        {isInactive && <span style={s.inactiveTag}>неактивний</span>}
                      </div>
                      <div style={s.userEmail}>{user.email}</div>
                    </div>
                    <div style={s.userMeta}>
                      {role ? <span style={s.roleBadge}>{role.name}</span> : <span style={s.noRole}>—</span>}
                    </div>
                    <div style={s.userActions}>
                      <button onClick={() => handleEdit(user)} style={s.actionBtn}>✏️ Редагувати</button>
                      {!isSelf && (
                        <button
                          onClick={() => handleToggleActive(user)}
                          disabled={toggling === user.id}
                          style={{ ...s.actionBtn, ...(isInactive ? s.actionBtnActivate : s.actionBtnDeactivate) }}
                        >
                          {isInactive ? '✅ Активувати' : '⏸ Деактивувати'}
                        </button>
                      )}
                      {!isSelf && (
                        <button
                          onClick={() => handleDelete(user)}
                          disabled={deleting === user.id}
                          style={{ ...s.actionBtn, color: '#b84040', borderColor: '#f5d0d0' }}
                        >
                          🗑 Видалити
                        </button>
                      )}
                    </div>
                  </div>
                )
              })}
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div style={s.pagination}>
              <button style={s.pageBtn} onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}>←</button>
              {Array.from({ length: totalPages }, (_, i) => i + 1).map(n => (
                <button
                  key={n}
                  style={{ ...s.pageBtn, ...(n === page ? s.pageBtnActive : {}) }}
                  onClick={() => setPage(n)}
                >
                  {n}
                </button>
              ))}
              <button style={s.pageBtn} onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}>→</button>
            </div>
          )}
        </div>
      </div>
    </AdminLayout>
  )
}

// ── Styles ────────────────────────────────────────────────────────────────────
const s = {
  page:      { maxWidth: '900px' },
  pageHeader:{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '28px' },
  pageTitle: { margin: '0 0 6px', fontSize: '24px', fontWeight: '700', color: '#2d1f14' },
  pageDesc:  { margin: 0, fontSize: '14px', color: '#9a7c63' },

  card: {
    background: '#fff', borderRadius: '14px', padding: '28px',
    boxShadow: '0 2px 16px rgba(92,61,46,0.07)', border: '1px solid #f0e4d4',
  },
  cardHeader: {
    display: 'flex', alignItems: 'center', gap: '10px',
    marginBottom: '18px', paddingBottom: '16px', borderBottom: '1px solid #f5ede3',
  },
  cardIcon:  { fontSize: '18px' },
  cardTitle: { fontSize: '15px', fontWeight: '700', color: '#2d1f14', flex: 1 },
  badge: {
    background: '#f0e6d8', color: '#8b6044',
    padding: '2px 10px', borderRadius: '20px', fontSize: '12px', fontWeight: '600',
  },

  filters: { display: 'flex', gap: '10px', marginBottom: '16px', flexWrap: 'wrap' },

  formReset: {
    width: '100%', margin: 0, padding: 0,
    border: 'none', borderRadius: 0, background: 'none', boxShadow: 'none', display: 'block',
  },
  formGrid:  { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 24px' },
  field:     { marginBottom: '18px' },
  label: {
    display: 'block', fontSize: '12px', fontWeight: '700',
    letterSpacing: '0.5px', textTransform: 'uppercase', color: '#8b6044', marginBottom: '8px',
  },
  selfNote:  { fontWeight: '400', textTransform: 'none', color: '#c4986a', letterSpacing: 0 },
  input: {
    width: '100%', padding: '11px 14px', border: '1.5px solid #e8d8c8',
    borderRadius: '8px', fontSize: '14px', color: '#2d1f14',
    background: '#fdfaf7', boxSizing: 'border-box', fontFamily: 'inherit', outline: 'none',
  },
  inputDisabled: { opacity: 0.5, cursor: 'not-allowed', background: '#f5ede3' },

  btnRow:    { display: 'flex', gap: '10px', marginTop: '8px' },
  btnPrimary:{
    padding: '11px 24px', background: 'linear-gradient(135deg, #6b4a32, #8b6044)',
    color: '#fff', border: 'none', borderRadius: '8px', fontSize: '14px',
    fontWeight: '600', cursor: 'pointer', fontFamily: 'inherit',
  },
  btnGhost: {
    padding: '11px 20px', background: 'transparent', color: '#8b6044',
    border: '1.5px solid #e8d8c8', borderRadius: '8px', fontSize: '14px',
    cursor: 'pointer', fontFamily: 'inherit',
  },

  empty: { color: '#b8a090', fontSize: '14px', padding: '16px 0', textAlign: 'center' },

  userList: { display: 'flex', flexDirection: 'column', gap: '6px' },
  userRow: {
    display: 'flex', alignItems: 'center', gap: '14px', padding: '12px 14px',
    borderRadius: '10px', background: '#fdfaf7', border: '1px solid #f5ede3',
    flexWrap: 'wrap',
  },
  userRowInactive: { background: '#f9f5f2', opacity: 0.7 },
  userAvatar: {
    width: '38px', height: '38px', borderRadius: '50%',
    background: 'linear-gradient(135deg, #c4986a, #8b6044)', color: '#fff',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontSize: '13px', fontWeight: '700', flexShrink: 0,
  },
  userAvatarInactive: { background: 'linear-gradient(135deg, #b0a090, #8a7868)' },
  userInfo:  { flex: 1, minWidth: 0 },
  userName:  { fontSize: '14px', fontWeight: '600', color: '#2d1f14', marginBottom: '2px', display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' },
  userEmail: { fontSize: '12px', color: '#9a7c63' },
  userMeta:  { flexShrink: 0 },

  youTag: {
    fontSize: '10px', fontWeight: '700', background: '#eef5ff', color: '#3a6dbf',
    padding: '2px 7px', borderRadius: '10px', letterSpacing: '0.3px',
  },
  inactiveTag: {
    fontSize: '10px', fontWeight: '700', background: '#f5e8e8', color: '#b84040',
    padding: '2px 7px', borderRadius: '10px', letterSpacing: '0.3px',
  },
  roleBadge: {
    background: '#f0e6d8', color: '#7a5030',
    padding: '4px 12px', borderRadius: '20px', fontSize: '12px', fontWeight: '600',
  },
  noRole: { fontSize: '13px', color: '#c8b0a0' },

  userActions: { display: 'flex', gap: '6px', flexShrink: 0, flexWrap: 'wrap' },
  actionBtn: {
    background: '#fff', border: '1px solid #e8d8c8', borderRadius: '7px', cursor: 'pointer',
    fontSize: '12px', padding: '6px 10px', color: '#6b4a32', fontFamily: 'inherit', whiteSpace: 'nowrap',
  },
  actionBtnDeactivate: { color: '#8b6044', borderColor: '#e8d8c8' },
  actionBtnActivate:   { color: '#2e7d52', borderColor: '#c8e8d4', background: '#f0faf4' },

  pagination: {
    display: 'flex', justifyContent: 'center', gap: '6px',
    marginTop: '20px', paddingTop: '16px', borderTop: '1px solid #f5ede3',
  },
  pageBtn: {
    padding: '6px 12px', border: '1px solid #e8d8c8', borderRadius: '7px',
    background: '#fff', cursor: 'pointer', fontSize: '13px', color: '#6b4a32',
    fontFamily: 'inherit',
  },
  pageBtnActive: { background: '#8b6044', color: '#fff', borderColor: '#8b6044', fontWeight: '700' },
}

export default AdminUsers
